/**
 * Modal de seguridad — activación voluntaria de la verificación en 2 pasos.
 *
 * Accesible desde el Header del Home. Muestra el estado actual
 * (users/{uid}.mfa — el self-read está exento del gate en las rules),
 * permite activar cualquiera de los dos métodos (reusa los paneles de
 * enroll/) y desactivar — salvo que el administrador la haya marcado como
 * obligatoria (mfa.required), en cuyo caso la desactivación se bloquea.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { Modal, Card, Button, Tag, Spin, Tooltip, Popconfirm, Typography, notification } from 'antd';
import { MobileOutlined, MailOutlined, ArrowLeftOutlined, SafetyOutlined } from '@ant-design/icons';

import { mfa } from '../../actions/auth/mfa';
import { MfaEnrollTotp } from './enroll/totp';
import { MfaEnrollEmail } from './enroll/email';

const { Paragraph, Text } = Typography;

const METHOD_LABELS = {
  totp: 'App de autenticación',
  email: 'Código por correo',
};

export const SecuritySettings = ({ visible, setVisible }) => {

  const dispatch = useDispatch();
  const firebase = useFirebase();

  const [loading, setLoading] = useState(true);
  const [disabling, setDisabling] = useState(false);
  const [mfaState, setMfaState] = useState({});    // users/{uid}.mfa
  const [method, setMethod] = useState(null);      // panel de activación abierto

  const loadState = useCallback(async () => {
    setLoading(true);
    try {
      const uid = firebase.auth().currentUser?.uid;
      const snap = await firebase.firestore().collection('users').doc(uid).get();
      setMfaState(snap.exists ? snap.data().mfa || {} : {});
    } catch (e) {
      console.warn('[mfa] no se pudo leer el estado:', e?.message);
      setMfaState({});
    } finally {
      setLoading(false);
    }
  }, [firebase]);

  useEffect(() => {
    if (visible) {
      setMethod(null);
      loadState();
    }
  }, [visible, loadState]);

  const onEnrolled = () => {
    setMethod(null);
    loadState();
  };

  const onDisable = async () => {
    setDisabling(true);
    try {
      await dispatch( mfa.disable() );
      notification.success({
        message: 'Verificación en 2 pasos desactivada',
        placement: 'topRight'
      });
      loadState();
    } catch ( error ) {
      notification.warning({
        message: 'No se pudo desactivar',
        description: error?.message,
        placement: 'topRight'
      });
    } finally {
      setDisabling(false);
    }
  };

  const active = Boolean(mfaState?.method);
  const required = mfaState?.required === true;

  return (
    <Modal
      forceRender
      title = { <span> <SafetyOutlined /> Seguridad — Verificación en 2 pasos </span> }
      footer = { null }
      maskClosable = { false }
      destroyOnHidden = { true }
      open = { visible }
      onCancel = { () => setVisible(false) } >

      { loading
        ? <div style = {{ textAlign: 'center', padding: 24 }} > <Spin /> </div>
        : active
          ? <div>
              <Paragraph>
                Estado: <Tag color = "green" > Activada </Tag>
                Método: <Text strong > { METHOD_LABELS[mfaState.method] || mfaState.method } </Text>
              </Paragraph>
              { required &&
                <Paragraph type = "secondary" >
                  El administrador estableció la verificación en 2 pasos como
                  obligatoria para su cuenta.
                </Paragraph> }
              <Tooltip title = { required ? 'Obligatoria por el administrador: no se puede desactivar' : null } >
                <Popconfirm
                  title = "¿Desactivar la verificación en 2 pasos?"
                  okText = "Desactivar"
                  cancelText = "Cancelar"
                  disabled = { required }
                  onConfirm = { onDisable } >
                  <Button danger disabled = { required } loading = { disabling } >
                    Desactivar
                  </Button>
                </Popconfirm>
              </Tooltip>
            </div>
          : method === null
            ? <div>
                <Paragraph>
                  Estado: <Tag> Desactivada </Tag>
                </Paragraph>
                <Paragraph>
                  Agregue un segundo paso de verificación para proteger su
                  cuenta al iniciar sesión.
                </Paragraph>
                <Card hoverable style = {{ marginBottom: 12 }} onClick = { () => setMethod('email') } >
                  <Card.Meta
                    avatar = { <MailOutlined style = {{ fontSize: 24 }} /> }
                    title = "Código por correo (recomendado)"
                    description = "Recibirá un código de 6 dígitos en su correo registrado cada vez que inicie sesión" />
                </Card>
                <Card hoverable onClick = { () => setMethod('totp') } >
                  <Card.Meta
                    avatar = { <MobileOutlined style = {{ fontSize: 24 }} /> }
                    title = "App de autenticación"
                    description = "Códigos generados por Google Authenticator, Microsoft Authenticator u otra app similar" />
                </Card>
              </div>
            : <div>
                <Button type = "link" icon = { <ArrowLeftOutlined /> } style = {{ padding: 0, marginBottom: 12 }}
                  onClick = { () => setMethod(null) } >
                  Elegir otro método
                </Button>
                { method === 'totp'
                  ? <MfaEnrollTotp onEnrolled = { onEnrolled } />
                  : <MfaEnrollEmail onEnrolled = { onEnrolled } /> }
              </div>
      }
    </Modal>
  );
};
